import { Injectable } from '@angular/core';
import { isEqual, omit, pull, some } from 'lodash-es';
import { Observable, of, Subject } from 'rxjs';
import { CutoutData, GridData, QuoteData, TierData } from './quote-data';
import { CloneQuoteData } from './quote-data-helper';

export const tiersChanged = (qd1: QuoteData, qd2: QuoteData) =>
  !isEqual(qd1.FloorData.TiersData.TierData, qd2.FloorData.TiersData.TierData);

export const liveLoadRequiresHeavyDecking = (
  qd: QuoteData,
  heavyLiveLoad: number
) =>
  some(
    qd.FloorData.TiersData.TierData,
    (tier: TierData) => tier.LiveLoad >= heavyLiveLoad
  );

export const gridChanged = (qd1: QuoteData, qd2: QuoteData) =>
  !isEqual(
    omit(qd1.FloorData.GridData, ['NotifyIfOtherBeamDirectionCheaper']),
    omit(qd2.FloorData.GridData, ['NotifyIfOtherBeamDirectionCheaper'])
  );

export const cutoutIsOutsideGrid = (cutout: CutoutData, grid: GridData) =>
  cutout.X + cutout.XDim > grid.XDim || cutout.Y + cutout.YDim > grid.YDim;

export const cutoutCanBeTrimmedToGrid = (cutout: CutoutData, grid: GridData) =>
  cutout.X < grid.XDim && cutout.Y < grid.YDim;

export const trimCutoutToGrid = (cutout: CutoutData, grid: GridData) => {
  if (cutout.X + cutout.XDim > grid.XDim) {
    cutout.XDim = grid.XDim - cutout.X;
  }
  if (cutout.Y + cutout.YDim > grid.YDim) {
    cutout.YDim = grid.YDim - cutout.Y;
  }
};

@Injectable()
export class GridChangeService {
  calculated$: Observable<boolean>;
  private calculated = new Subject<boolean>();
  private previousQd: QuoteData;

  constructor() {
    this.calculated$ = this.calculated.asObservable();
  }

  calculate(qd: QuoteData, heavyLoadTypeDefault: any): Observable<QuoteData> {
    if (this.previousQd) {
      if (gridChanged(this.previousQd, qd)) {
        for (const tierData of qd.FloorData.TiersData.TierData) {
          const cutoutsToRemove = [];
          for (const cutout of tierData.CutoutData) {
            if (cutoutIsOutsideGrid(cutout, qd.FloorData.GridData)) {
              if (cutoutCanBeTrimmedToGrid(cutout, qd.FloorData.GridData)) {
                trimCutoutToGrid(cutout, qd.FloorData.GridData);
              } else {
                cutoutsToRemove.push(cutout);
              }
            }
          }
          pull(tierData.CutoutData, ...cutoutsToRemove);
        }
        qd.TransformData = [];
      }
      if (
        tiersChanged(this.previousQd, qd) &&
        liveLoadRequiresHeavyDecking(qd, heavyLoadTypeDefault.liveLoad)
      ) {
        qd.FloorData.DeckingData.DeckingSection =
          heavyLoadTypeDefault.deckingSection.id;
        qd.FloorData.DeckingData.DeckingCentres = heavyLoadTypeDefault.centres;
      }
    }
    this.previousQd = CloneQuoteData(qd);
    return of(qd);
  }
}
